// =====================================================
// WEBSOCKET URL CONFIGURATION
// Centralized WebSocket URL generation with automatic protocol detection
// =====================================================

/**
 * Get environment variable - works in both test and production
 * Falls back to provided default value
 */
function getEnv(key: string, defaultValue?: string): string | undefined {
  // In test mode, look for env on a global test object
  if (process.env.NODE_ENV === 'test' && (globalThis as any).__testEnv__) {
    return (globalThis as any).__testEnv__[key] ?? defaultValue;
  }
  // @ts-ignore - import.meta.env is Vite-specific
  return import.meta.env?.[key] ?? defaultValue;
}

/**
 * Extract host and optional path from an API URL (without protocol)
 */
function extractHostAndPath(apiURL: string): string {
  // Remove protocol (http://, https://, ws://, wss://)
  return apiURL.replace(/^https?:\/\//, '').replace(/^wss?:\/\//, '');
}

/**
 * Determine the WebSocket protocol (ws or wss) based on API URL
 */
function getWebSocketProtocol(apiURL: string): 'ws' | 'wss' {
  // If API URL uses HTTPS or WSS, use WSS for secure WebSocket
  // If API URL uses HTTP or WS, use WS for insecure WebSocket
  return apiURL.startsWith('https') || apiURL.startsWith('wss') ? 'wss' : 'ws';
}

/**
 * Get the WebSocket base URL from the current environment configuration.
 * Derives the URL from VITE_API_URL with proper protocol detection.
 *
 * @returns The WebSocket base URL (e.g., "wss://api.example.com")
 *
 * @example
 * // In production with HTTPS
 * getWebSocketBaseURL() // "wss://api.example.com"
 *
 * // In development with HTTP
 * getWebSocketBaseURL() // "ws://localhost:8787"
 */
export function getWebSocketBaseURL(): string {
  const apiURL = getEnv('VITE_API_URL') || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173');
  const protocol = getWebSocketProtocol(apiURL);
  const hostAndPath = extractHostAndPath(apiURL);
  return `${protocol}://${hostAndPath}`;
}

/**
 * Get a WebSocket URL for a specific tenant and optional path.
 * Automatically handles protocol (ws:// vs wss://) based on the API URL.
 *
 * @param tenantId - The tenant ID for the WebSocket connection
 * @param path - Optional additional path (e.g., "/collaboration")
 * @returns The complete WebSocket URL
 *
 * @example
 * // Production
 * getWebSocketURL('tenant-123')
 * // "wss://api.example.com/tenant/tenant-123/websocket"
 *
 * getWebSocketURL('tenant-123', '/collaboration')
 * // "wss://api.example.com/tenant/tenant-123/websocket/collaboration"
 *
 * // Development
 * getWebSocketURL('tenant-456')
 * // "ws://localhost:8787/tenant/tenant-456/websocket"
 */
export function getWebSocketURL(tenantId: string, path: string = ''): string {
  const baseURL = getWebSocketBaseURL();
  const wsPath = path && !path.startsWith('/') ? `/${path}` : path;
  // Remove trailing slash from baseURL to avoid double slashes
  const trimmedBaseURL = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
  return `${trimmedBaseURL}/tenant/${tenantId}/websocket${wsPath}`;
}

/**
 * Get a WebSocket URL for GraphQL subscriptions.
 * Uses VITE_GRAPHQL_WS_URL if available, otherwise derives from VITE_GRAPHQL_URL.
 *
 * @returns The GraphQL WebSocket URL for subscriptions
 *
 * @example
 * // Production
 * getGraphQLWebSocketURL()
 * // "wss://api.example.com/graphql"
 */
export function getGraphQLWebSocketURL(): string {
  // Explicit GraphQL WS URL takes precedence
  const wsURL = getEnv('VITE_GRAPHQL_WS_URL');
  if (wsURL) {
    return wsURL;
  }

  // Derive from GraphQL URL
  const graphqlURL = getEnv('VITE_GRAPHQL_URL');
  if (graphqlURL) {
    const protocol = getWebSocketProtocol(graphqlURL);
    const hostAndPath = extractHostAndPath(graphqlURL);
    return `${protocol}://${hostAndPath}`;
  }

  // Fall back to base URL with /graphql path
  return `${getWebSocketBaseURL()}/graphql`;
}

/**
 * Build a complete WebSocket URL from a base URL and path components.
 * Helper for custom WebSocket endpoints.
 *
 * @param baseURL - The base WebSocket URL
 * @param pathSegments - Path segments to join
 * @returns The complete WebSocket URL
 */
export function buildWebSocketURL(baseURL: string, ...pathSegments: string[]): string {
  // Determine protocol - preserve ws:// or wss://, convert http/https to ws/wss
  let protocol: 'ws' | 'wss';
  if (baseURL.startsWith('wss://') || baseURL.startsWith('https://')) {
    protocol = 'wss';
  } else {
    protocol = 'ws';
  }

  // Extract host (remove protocol and any existing path)
  const host = extractHostAndPath(baseURL).split('/')[0];

  // Build the path from segments, cleaning slashes
  const path = pathSegments
    .map((segment) => segment.replace(/^\/+|\/+$/g, ''))
    .filter(Boolean)
    .join('/');

  return `${protocol}://${host}${path ? `/${path}` : ''}`;
}

/**
 * Check if the current environment uses secure WebSocket (WSS).
 *
 * @returns true if using WSS, false if using WS
 */
export function isSecureWebSocket(): boolean {
  return getWebSocketBaseURL().startsWith('wss:');
}

/**
 * Set test environment variables (for testing only)
 * @internal
 */
export function __setTestEnv(env: Record<string, string | undefined>) {
  if (process.env.NODE_ENV === 'test') {
    (globalThis as any).__testEnv__ = env;
  }
}

/**
 * Clear test environment variables (for testing only)
 * @internal
 */
export function __clearTestEnv() {
  if (process.env.NODE_ENV === 'test') {
    delete (globalThis as any).__testEnv__;
  }
}
